import { useState, useEffect, useCallback, useRef } from 'react';
import { usePermissions } from '@/contexts/unified-permissions';
import { toast } from 'sonner';
import type { Message, UseChatMessagesReturn, ChatRealtimeSubscription } from '../types';
import { fetchMessages, subscribeToMessages } from '../services/chatService';

// Retry configuration for WebSocket connections
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2
};

/**
 * Hook for managing chat messages between current user and another user
 * Includes realtime updates via Supabase Realtime
 */
export const useChatMessages = (otherUserId?: string): UseChatMessagesReturn => {
  const { user } = usePermissions();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const subscriptionRef = useRef<ChatRealtimeSubscription | null>(null);
  const retryCountRef = useRef<number>(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUnmountedRef = useRef<boolean>(false);

  // Load messages from database
  const loadMessages = useCallback(async () => {
    if (!user?.id || !otherUserId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fetchedMessages = await fetchMessages(user.id, otherUserId);
      setMessages(fetchedMessages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to load messages');
      console.error('Error loading messages:', errorMessage);
      setError(errorMessage);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [user?.id, otherUserId]);

  // Handle new messages from realtime subscription
  const handleNewMessage = useCallback((newMessage: Message) => {
    setMessages(prevMessages => {
      // Check if message already exists (avoid duplicates)
      const exists = prevMessages.some(msg => msg.id === newMessage.id);
      if (exists) {
        // Update existing message (for read status updates)
        return prevMessages.map(msg =>
          msg.id === newMessage.id ? newMessage : msg
        );
      }

      // Check if this is a real message that should replace an optimistic one
      // Look for optimistic messages with similar content and timestamp (within 5 seconds)
      const optimisticIndex = prevMessages.findIndex(msg =>
        msg.id.startsWith('temp-') &&
        msg.content === newMessage.content &&
        msg.senderUserId === newMessage.senderUserId &&
        msg.receiverUserId === newMessage.receiverUserId &&
        Math.abs(msg.createdAt.getTime() - newMessage.createdAt.getTime()) < 5000
      );

      if (optimisticIndex !== -1) {
        // Replace optimistic message with real one
        const updatedMessages = [...prevMessages];
        updatedMessages[optimisticIndex] = newMessage;
        return updatedMessages.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      }

      // Add new message in chronological order
      const updatedMessages = [...prevMessages, newMessage];
      return updatedMessages.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    });
  }, []);

  // Add optimistic message immediately when sending
  const addOptimisticMessage = useCallback((content: string, senderId: string, receiverId: string) => {
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID
      senderUserId: senderId,
      receiverUserId: receiverId,
      content: content.trim(),
      createdAt: new Date(),
      // No readAt since it's just sent
    };

    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, optimisticMessage];
      return updatedMessages.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    });

    return optimisticMessage.id;
  }, []);

  // Remove optimistic message if send fails
  const removeOptimisticMessage = useCallback((tempId: string) => {
    setMessages(prevMessages =>
      prevMessages.filter(msg => msg.id !== tempId)
    );
  }, []);

  // Handle realtime subscription errors
  const handleRealtimeError = useCallback((err: Error) => {
    console.error('Realtime subscription error:', err);
    setError(err);
    // Don't show toast for realtime errors as they're often temporary
  }, []);

  // Calculate retry delay with exponential backoff
  const calculateRetryDelay = useCallback((retryCount: number): number => {
    const delay = RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, retryCount);
    return Math.min(delay, RETRY_CONFIG.maxDelay);
  }, []);

  // Setup WebSocket subscription with retry logic
  const setupSubscription = useCallback(() => {
    if (!user?.id || !otherUserId || isUnmountedRef.current) {
      return;
    }

    // Clean up existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.cleanup();
      subscriptionRef.current = null;
    }

    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    try {
      const subscription = subscribeToMessages(
        user.id,
        otherUserId,
        handleNewMessage,
        (err: Error) => {
          handleRealtimeError(err);

          // Retry logic for subscription errors
          if (!isUnmountedRef.current && retryCountRef.current < RETRY_CONFIG.maxRetries) {
            const delay = calculateRetryDelay(retryCountRef.current);
            console.warn(`⚠️ Chat messages subscription failed (attempt ${retryCountRef.current + 1}/${RETRY_CONFIG.maxRetries}), retrying in ${delay}ms...`);

            retryCountRef.current++;
            retryTimeoutRef.current = setTimeout(() => {
              if (!isUnmountedRef.current) {
                setupSubscription();
              }
            }, delay);
          } else if (retryCountRef.current >= RETRY_CONFIG.maxRetries) {
            console.error('❌ Chat messages subscription failed after maximum retries');
            setError(new Error('Failed to establish chat connection after multiple attempts'));
          }
        }
      );

      subscriptionRef.current = subscription;

      // Reset retry count on successful setup
      retryCountRef.current = 0;
      console.log('✅ Chat messages subscription established');

    } catch (err) {
      console.error('Error setting up messages subscription:', err);
      if (!isUnmountedRef.current && retryCountRef.current < RETRY_CONFIG.maxRetries) {
        const delay = calculateRetryDelay(retryCountRef.current);
        retryCountRef.current++;
        retryTimeoutRef.current = setTimeout(() => {
          if (!isUnmountedRef.current) {
            setupSubscription();
          }
        }, delay);
      }
    }
  }, [user?.id, otherUserId, handleNewMessage, handleRealtimeError, calculateRetryDelay]);

  // Setup realtime subscription
  useEffect(() => {
    isUnmountedRef.current = false;

    if (!user?.id || !otherUserId) {
      return;
    }

    // Reset retry count when user or otherUserId changes
    retryCountRef.current = 0;

    // Setup subscription with retry logic
    setupSubscription();

    // Cleanup on unmount or dependency change
    return () => {
      isUnmountedRef.current = true;

      // Clear retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      // Clean up subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.cleanup();
        subscriptionRef.current = null;
      }
    };
  }, [user?.id, otherUserId, setupSubscription]);

  // Load messages when dependencies change
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Refetch function for manual refresh
  const refetch = useCallback(async () => {
    await loadMessages();
  }, [loadMessages]);

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.cleanup();
      }
    };
  }, []);

  return {
    messages,
    loading,
    error,
    refetch,
    addOptimisticMessage,
    removeOptimisticMessage,
  };
};
